import { IPDFItemStyle, IPDFStyleType } from "@Models/IPdfItem";
import { StyleSheet } from "@react-pdf/renderer";

const HEADING_BOTTOM_MARGIN = 10;

const HEADING_OPTIONS = {
    fontWeight: 700,
    lineHeight: 1.5,
    marginBottom: HEADING_BOTTOM_MARGIN,
};

const P = {
    fontFamily: "Roboto",
    fontSize: 8,
    lineHeight: 1.5,
};
const BOLD = { fontFamily: "RobotoBold", fontStyle: "normal" };
const NORMAL = { fontFamily: "Roboto", fontStyle: "normal" };

const BULLET = { ...P, marginLeft: 15 };
const OUTER_BORDER_COLOR = "#DEE2E6";
const BORDER_COLOR = "#D9D9D9";

const usePDFStyles = (report: string, theme: string) => {
    const stylesArrayToObject = (stylesArr: IPDFItemStyle[]) =>
        Object.assign({}, ...stylesArr.flatMap((style) => styles?.[style] ?? {}));

    const getStyles = (pdfStyle: IPDFStyleType) => {
        switch (typeof pdfStyle) {
            case "string":
                return styles?.[pdfStyle];
            case "object":
                return Array.isArray(pdfStyle) ? stylesArrayToObject(pdfStyle) : pdfStyle;
        }
    };

    const baseStyles = {
        body: { padding: 50 },
        block: {},
        blockView: {
            flexDirection: "row",
        },
        bullet: BULLET,
        centred: {
            alignItems: "center",
            textAlign: "center",
        },
        underlined: {
            textDecoration: "underline",
        },
        numbered: BULLET,
        p: { ...P, marginBottom: 14 },
        pageWrap: {},
        strong: { ...P, ...BOLD, marginBottom: 14 },
        spacer: { marginBottom: 42 },
        viewbox: {},
        left: {
            alignItems: "left",
            textAlign: "left",
        },
        right: {
            alignItems: "right",
            textAlign: "right",
        },
        footerLogo: {},
        glcLogo: { width: 200, height: "auto" },
        footerView: {},

        // headings
        title: {
            fontFamily: "RobotoBold",
            fontSize: 24,
            fontWeight: 700,
            lineHeight: 1.5,
        },
        h1: {
            ...HEADING_OPTIONS,
            color: "#555555",
            fontFamily: "RobotoBoldItalic",
            fontSize: 18,
            fontStyle: "italic",
        },
        h2: {
            ...HEADING_OPTIONS,
            color: "#111111",
            fontFamily: "RobotoBold",
            fontSize: 15,
            textDecoration: "underline",
            textDecorationColor: "#1890ff",
        },
        h3: {
            ...HEADING_OPTIONS,
            color: "#555555",
            fontFamily: "RobotoBold",
            fontSize: 12,
        },
        h4: {
            ...HEADING_OPTIONS,
            fontFamily: "RobotoBold",
            fontSize: 10,
        },
        h5: {
            ...HEADING_OPTIONS,
            fontFamily: "RobotoBold",
            fontSize: 10,
        },
        h6: {
            ...HEADING_OPTIONS,
            fontFamily: "Roboto",
            fontSize: 10,
        },
        italic: {
            fontFamily: "RobotoItalic",
            fontSize: 12,
        },
        text: {
            fontFamily: "Roboto",
            fontSize: 12,
        },

        letterhead: { marginBottom: 10, width: "100%" },
        section: {},

        // table
        tableRow: {
            alignItems: "stretch",
            borderBottomColor: OUTER_BORDER_COLOR,
            borderBottomWidth: 1,
            borderLeftColor: BORDER_COLOR,
            borderLeftWidth: 1,
            flexDirection: "row",
        },
        tableCell: {
            borderRightColor: BORDER_COLOR,
            borderRightWidth: 1,
            flex: "1 1 0",
            padding: 8,
            textAlign: "left",
        },
        titlePage: {},
    } as const;

    const certificateStyles = {
        body: { padding: "5vw" },
        h1: {
            color: "#000",
            fontFamily: "RobotoBold",
            fontSize: 40,
            fontWeight: 700,
            lineHeight: 1.5,
            marginTop: 10,
        },
        h2: { ...baseStyles.title, marginBottom: 0, lineHeight: 1.25 },
        h3: { ...baseStyles.h1, fontSize: 24, ...BOLD, marginTop: 10 },
        h4: { ...baseStyles.h1, ...BOLD },
        h5: { ...baseStyles.h1, color: "#000", fontSize: 14, ...NORMAL, marginBottom: 0 },
        h6: { ...baseStyles.h1, fontSize: 14, ...BOLD },
        pageWrap: {
            border: "1 solid #000",
            height: "92.5vh",
            position: "absolute",
            width: "90vw",
        },
        signatureImage: { width: "3cm" },
        footerView: {
            bottom: 10,
            flexDirection: "row",
            justifyContent: "space-between",
            paddingLeft: "2.75vw",
            position: "absolute",
            width: "87.25vw",
        },
        footerLogo: { width: "4cm" },
        viewbox: {
            minHeight: "100%",
            maxHeight: "100%",
            padding: 20,
        },
        strong: { ...baseStyles.strong, fontSize: 10 },
        titlePage: {
            paddingRight: 0,
            paddingLeft: 0,
        },
    };

    const assessmentReportStyles = {
        inovusLogo: {
            height: 80,
            paddingLeft: 50,
        },
    };

    const themeStyles = {
        certificateStyles,
    };

    const reportStyles = {
        assessmentReportStyles,
    };

    const styles = StyleSheet.create({
        ...baseStyles,
        ...(themeStyles?.[`${theme}Styles`] ?? {}),
        ...(reportStyles?.[`${report}ReportStyles`] ?? {}),
    }) as typeof baseStyles; // recasting because Stylesheet.create does not retain type information

    return { getStyles, styles };
};

export default usePDFStyles;
